import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useSearchParams } from "react-router";
import { Box, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { startOfDay } from "date-fns";
import {
  DialogComponentProps,
  useDialogService,
  Typography,
} from "@platform/design-system";
import { useApi } from "@platform/api";
import { ExportRunner, useExportActions } from "@platform/btx-plan-designer";
import {
  useCoverageNoteDrafts,
  useUpsertCoverageNote,
  useDeleteCoverageNote,
  useRfpNote,
  useUpsertRfpNote,
  useAgGridHiddenColumns,
} from "@platform/hooks";
import { logger } from "@platform/browser-logger";

import { useCoverageOverview } from "../../hooks/useCoverageOverview";
import { buildCoverageNoteSections } from "../../coverageNoteSections";

import { ConfirmationHiddenColumnsDialog } from "./ConfirmationHiddenColumnsDialog";
import { SubmissionAlert } from "./SubmissionAlert";
import { SubmitBlockingAlerts } from "./SubmitBlockingAlerts";
import { OfflineSubmissionToggle } from "./OfflineSubmissionToggle";
import {
  offlineSubmissionSchema,
  type OfflineSubmissionFormData,
  type OfflinePlansQuotedCoverage,
} from "./OfflineSubmissionForm";
import { FooterButtons } from "./FooterButtons";
import StepContent from "./StepContent";
import { type EditedReviewFields } from "./ReviewSection";
import {
  StepState,
  initialStepState,
  getNextStep,
  getPreviousStep,
  isLastStep,
  STEP_ORDER,
  STEP_CARRIERS,
  STEP_REVIEW,
} from "./stepMachine";
import {
  useSendRfpData,
  useCarrierDerivedState,
  validateSubmissionData,
  useSendRfpSubmission,
} from "./hooks";
import {
  ECO_RFP_ID_PARAM,
  DRAWER_TITLE,
  NOTES_DIALOG_TYPE,
  NOTES_DRAWER_WIDTH,
  NOTES_DRAWER_WIDTH_UNIT,
  SUBMISSION_IDLE,
} from "./constants";

import type ExcelJS from "exceljs";
import type { NotesTab } from "./constants";
import type { SubmissionStatus, ExportStatus } from "./api";
import type { SendRfpCarrier } from "./api/types";

interface SendRFPDrawerData {
  ecoRfpId: string;
  brokerPortalRfpId: string;
}

// Selected carriers whose group is at or above their configured API-submission
// limit. Uses the Review-edited eligible count when present, else the client's.
// Offline submission bypasses the limit, so it returns none. (PB-690)
export const getSelectedCarriersOverLimit = (
  carriers: SendRfpCarrier[],
  selectedCarrierIds: string[],
  editedEmployees: string | undefined,
  clientEmployees: number | null | undefined,
  isOfflineSubmission: boolean,
): SendRfpCarrier[] => {
  if (isOfflineSubmission) return [];

  const parsedEdited = editedEmployees
    ? Number.parseInt(editedEmployees, 10)
    : NaN;
  const eligibleEmployees = Number.isNaN(parsedEdited)
    ? (clientEmployees ?? null)
    : parsedEdited;
  if (eligibleEmployees === null) return [];

  return carriers.filter(
    (carrier) =>
      selectedCarrierIds.includes(carrier.id) &&
      carrier.maxEligibleLives !== null &&
      eligibleEmployees >= carrier.maxEligibleLives,
  );
};

export const computeCanSubmit = (
  stepState: StepState,
  selectedCarriersWithMissingSalesRep: SendRfpCarrier[],
  validationResult: { isValid: boolean },
  isOfflineFormValid: boolean,
  selectedCarriersOverLimit: SendRfpCarrier[] = [],
): boolean => {
  const hasNoMissingSalesReps =
    selectedCarriersWithMissingSalesRep.length === 0;
  const hasSelectedCarriers = stepState.selectedCarriers.length > 0;

  if (stepState.isOfflineSubmission) {
    return hasNoMissingSalesReps && hasSelectedCarriers && isOfflineFormValid;
  }

  return (
    hasNoMissingSalesReps &&
    validationResult.isValid &&
    hasSelectedCarriers &&
    stepState.selectedCoverages.length > 0 &&
    selectedCarriersOverLimit.length === 0
  );
};

/**
 * Human-readable list of what still blocks an offline submission, for the
 * disabled Submit button's tooltip. Derived from the offline Zod schema so the
 * labels never drift from the actual validation, with the empty "plans quoted"
 * coverages named individually since the analyst can't otherwise tell which of
 * the per-coverage boxes are still blank.
 */
export const getOfflineMissingFields = (
  offlineValues: Partial<OfflineSubmissionFormData>,
  plansQuotedCoverages: OfflinePlansQuotedCoverage[],
  hasSelectedCarriers: boolean,
): string[] => {
  const missing: string[] = [];
  if (!hasSelectedCarriers) missing.push("At least one carrier");

  const result = offlineSubmissionSchema.safeParse(offlineValues);
  if (!result.success) {
    const missingPlanLabels: string[] = [];
    for (const issue of result.error.issues) {
      const [field, planType] = issue.path;
      switch (field) {
        case "offlineReason":
          missing.push("Reason for offline submission");
          break;
        case "offlineDescription":
          missing.push("Description (minimum 25 characters)");
          break;
        case "dateSentToCarriers":
          missing.push("Date Sent to Carriers");
          break;
        case "offlinePlansQuoted":
          if (typeof planType === "string") {
            const label =
              plansQuotedCoverages.find(
                (coverage) => coverage.type === planType,
              )?.label ?? planType;
            if (!missingPlanLabels.includes(label)) {
              missingPlanLabels.push(label);
            }
          }
          break;
      }
    }
    if (missingPlanLabels.length > 0) {
      missing.push(`Total plans quoted (${missingPlanLabels.join(", ")})`);
    }
  }

  return [...new Set(missing)];
};

/**
 * Coverage-note drafts are fetched once the analyst can see the Review cards:
 * the Review step online, or the always-visible Review in the offline flow.
 */
const shouldFetchCoverageNotes = (
  stepState: StepState,
  noteSectionCount: number,
): boolean =>
  (stepState.currentStep === STEP_REVIEW || stepState.isOfflineSubmission) &&
  noteSectionCount > 0;

/**
 * The list fed to the Submit tooltip: offline validation when the offline toggle
 * is on, otherwise the online required-field validation.
 */
const getMissingFields = (
  stepState: StepState,
  offlineValues: Partial<OfflineSubmissionFormData>,
  plansQuotedCoverages: OfflinePlansQuotedCoverage[],
  onlineMissingFields: string[],
): string[] =>
  stepState.isOfflineSubmission
    ? getOfflineMissingFields(
        offlineValues,
        plansQuotedCoverages,
        stepState.selectedCarriers.length > 0,
      )
    : onlineMissingFields;

/**
 * Splits a coverage note key (`${coverageId}:${benefitPlanType}`) back into its
 * parts. A trailing empty segment means the note has no plan type (e.g. vision).
 */
const parseCoverageNoteKey = (
  noteKey: string,
): { coverageId: string; benefitPlanType?: string } => {
  const keys = noteKey.split(":");
  if (keys.length === 1) return { coverageId: noteKey };
  return {
    coverageId: keys[0],
    benefitPlanType: keys[1],
  };
};

const SendRfpDrawer = ({
  data,
  onClose,
}: DialogComponentProps): React.JSX.Element => {
  const drawerData = data as unknown as SendRFPDrawerData | undefined;

  // Get IDs from URL as fallback when dialog is opened via URL query params
  const { id: urlRfpId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const urlEcoRfpId = searchParams.get(ECO_RFP_ID_PARAM) ?? "";

  // Prefer dialog data, fall back to URL params
  const ecoRfpId = drawerData?.ecoRfpId || urlEcoRfpId;
  const brokerPortalRfpId = drawerData?.brokerPortalRfpId || urlRfpId || "";

  const { openDialog } = useDialogService();
  const [stepState, setStepState] = useState<StepState>(initialStepState);
  const [exportStatus, setExportStatus] = useState<ExportStatus>("idle");
  const [rfpNotes, setRfpNotes] = useState("");
  const [editedFields, setEditedFields] = useState<EditedReviewFields | null>(
    null,
  );
  const [isReviewEditing, setIsReviewEditing] = useState(false);
  const [showHiddenColumnsDialog, setShowHiddenColumnsDialog] = useState(false);
  const [hiddenColumnCoverageLabels, setHiddenColumnCoverageLabels] = useState<
    string[]
  >([]);

  // Fetch RFP-level note from backend
  const { data: savedRfpNote } = useRfpNote({ ecoRfpId });

  // Seed RFP notes state from server on initial load (once)
  const rfpNoteSyncedRef = useRef(false);
  useEffect(() => {
    if (!rfpNoteSyncedRef.current && savedRfpNote) {
      setRfpNotes(savedRfpNote.content);
      rfpNoteSyncedRef.current = true;
    }
  }, [savedRfpNote]);

  // RFP note mutation
  const upsertRfpNoteMutation = useUpsertRfpNote({
    ecoRfpId,
    onError: (error) => {
      logger.error("Failed to upsert RFP note", error);
      // Revert to server-known value on error
      setRfpNotes(savedRfpNote?.content ?? "");
    },
  });

  const {
    control: offlineFormControl,
    formState: { isValid: isOfflineFormValid },
    reset: resetOfflineForm,
    getValues: getOfflineFormValues,
    watch: watchOfflineForm,
  } = useForm<OfflineSubmissionFormData>({
    defaultValues: {
      offlineReason: undefined,
      offlineDescription: "",
      dateSentToCarriers: startOfDay(new Date()),
      offlinePlansQuoted: {},
    },
    resolver: zodResolver(offlineSubmissionSchema),
    mode: "onTouched",
  });

  // Fetch all RFP data using extracted hook
  const {
    carriers,
    coveragesWithCompletion,
    clientDetails,
    brokerageDetails,
    submissionData,
    analystDocsFolderUrl,
    isLoading,
    isError,
    getInitialCoverageIds,
    markCoveragesInitialized,
    areCoveragesInitialized,
  } = useSendRfpData({ ecoRfpId, brokerPortalRfpId });

  const { downloadExport, uploadSubmissionExport } = useExportActions({
    rfpName: submissionData?.clientName ?? "export",
  });

  // Coverages whose notes the Review step shows. Online scopes to the plans the
  // analyst selected; offline has no coverage-selection step and resets the
  // selection, so it scopes to every complete coverage on the RFP instead.
  const noteScopeCoverages = useMemo(
    () =>
      coveragesWithCompletion.filter(
        (coverage) =>
          coverage.isComplete &&
          (stepState.isOfflineSubmission ||
            stepState.selectedCoverages.includes(coverage.id)),
      ),
    [
      coveragesWithCompletion,
      stepState.isOfflineSubmission,
      stepState.selectedCoverages,
    ],
  );

  // Pass the configured axios instance so this shares the same working request
  // as useSendRfpData rather than the hook's bare-axios default (no baseURL,
  // which would resolve the relative request to the SPA fallback).
  const apiClient = useApi();
  const { coverageQuery } = useCoverageOverview({
    ecoRfpId,
    axiosInstance: apiClient,
  });

  // Chip ids are per-plan note keys, so notes are scoped to the exact plans the
  // user selected rather than every plan of the coverage.
  const selectedNoteKeys = useMemo(
    () => new Set(noteScopeCoverages.map((coverage) => coverage.id)),
    [noteScopeCoverages],
  );

  const noteSections = useMemo(
    () => buildCoverageNoteSections(coverageQuery.data, selectedNoteKeys),
    [coverageQuery.data, selectedNoteKeys],
  );

  const {
    notes: coverageNotes,
    baselineNotes: baselineCoverageNotes,
    setCoverageNotes,
    resetCoverageNotes,
  } = useCoverageNoteDrafts({
    ecoRfpId,
    coverages: noteSections.map((section) => ({
      id: section.coverageId,
      benefitPlanType: section.benefitPlanType,
      noteKey: section.noteKey,
    })),
    enabled: shouldFetchCoverageNotes(stepState, noteSections.length),
  });

  // Coverage note mutations
  const upsertCoverageNoteMutation = useUpsertCoverageNote({
    ecoRfpId,
    onError: (error) => {
      logger.error("Failed to upsert coverage note", error);
      resetCoverageNotes();
    },
  });
  const deleteCoverageNoteMutation = useDeleteCoverageNote({
    ecoRfpId,
    onError: (error) => {
      logger.error("Failed to delete coverage note", error);
      resetCoverageNotes();
    },
  });

  // Auto-select all complete coverages on initial load. Waits for isLoading
  // to clear so it reads completion data from the coverage summary rather
  // than the pre-summary default (isComplete: false for every coverage) —
  // otherwise it locks in an empty selection and never re-runs.
  useEffect(() => {
    if (
      !isLoading &&
      !isError &&
      !areCoveragesInitialized() &&
      coveragesWithCompletion.length > 0 &&
      stepState.selectedCoverages.length === 0
    ) {
      const completeCoverageIds = getInitialCoverageIds();
      setStepState((prev) => ({
        ...prev,
        selectedCoverages: completeCoverageIds,
      }));
      markCoveragesInitialized();
    }
  }, [
    isLoading,
    isError,
    coveragesWithCompletion,
    stepState.selectedCoverages.length,
    getInitialCoverageIds,
    markCoveragesInitialized,
    areCoveragesInitialized,
  ]);

  // Derive carrier state (warnings, selected unconnected carriers)
  const {
    showCarrierWarning,
    selectedCarriersWithMissingSalesRep,
    selectedUnconnectedCarriers,
    selectedConnectedCarriers,
  } = useCarrierDerivedState(carriers, stepState.selectedCarriers);

  // Validate all required fields
  const validationResult = validateSubmissionData(
    submissionData,
    clientDetails,
    brokerageDetails,
    editedFields,
  );

  const clientEligibleEmployees = clientDetails?.numberOfEmployees ?? null;

  // Selected carriers whose group now exceeds their API limit — honoring an
  // Eligible Employees edit made in Review, since the carrier-selection block
  // only runs when carriers are picked (PB-690).
  const selectedCarriersOverLimit = getSelectedCarriersOverLimit(
    carriers,
    stepState.selectedCarriers,
    editedFields?.client.numberOfEmployees,
    clientEligibleEmployees,
    stepState.isOfflineSubmission,
  );

  const canSubmit = computeCanSubmit(
    stepState,
    selectedCarriersWithMissingSalesRep,
    validationResult,
    isOfflineFormValid,
    selectedCarriersOverLimit,
  );

  // Map selected plan chips to their coverage type strings for the export and
  // submit filter. Multiple plans of one coverage share a type, so dedupe: the
  // backend submits by coverage, not by plan.
  const selectedCoverageTypes = useMemo(
    () => [
      ...new Set(
        coveragesWithCompletion
          .filter((c) => stepState.selectedCoverages.includes(c.id))
          .map((c) => c.type),
      ),
    ],
    [coveragesWithCompletion, stepState.selectedCoverages],
  );

  // One plans-quoted entry per line of coverage on the RFP (XTRM-2022). Plans
  // sharing a coverage type (Dental PPO/HMO chips) collapse to one field labeled
  // with the first chip's label for that type.
  const offlinePlansQuotedCoverages = useMemo(
    () =>
      coveragesWithCompletion
        .filter(
          (coverage, index, all) =>
            all.findIndex((other) => other.type === coverage.type) === index,
        )
        .map((coverage) => ({ type: coverage.type, label: coverage.label })),
    [coveragesWithCompletion],
  );

  // What still blocks an offline submission, shown in the disabled Submit
  // tooltip. watch() so the list updates live as the analyst fills the form.
  const offlineFormValues = watchOfflineForm();
  const offlineMissingFields = getMissingFields(
    stepState,
    offlineFormValues,
    offlinePlansQuotedCoverages,
    validationResult.missingFields,
  );

  // Group the selected plan chips by coverage type so the backend can submit a
  // subset of a coverage's plans (e.g. Dental PPO without Dental HMO). Only
  // coverages that expose plan variants contribute entries; coverage types
  // absent from this map submit all their plans.
  const selectedCoveragePlanTypes = useMemo(
    () =>
      coveragesWithCompletion.reduce<Record<string, string[]>>(
        (acc, coverage) => {
          if (
            !stepState.selectedCoverages.includes(coverage.id) ||
            !coverage.benefitPlanType
          ) {
            return acc;
          }
          (acc[coverage.type] ??= []).push(coverage.benefitPlanType);
          return acc;
        },
        {},
      ),
    [coveragesWithCompletion, stepState.selectedCoverages],
  );

  // Handle RFP submission using extracted hook
  const {
    submissionStatus,
    submissionError,
    beginSubmission,
    failSubmission,
    handleSubmit,
  } = useSendRfpSubmission({
    ecoRfpId,
    brokerPortalRfpId,
    carriers,
    selectedCarriers: stepState.selectedCarriers,
    selectedCoverageTypes,
    selectedCoveragePlanTypes,
    selectedUnconnectedCarriers,
    selectedConnectedCarriers,
    isOfflineSubmission: stepState.isOfflineSubmission,
    submissionData,
    getOfflineFormValues,
  });

  const { hiddenColumnsCount } = useAgGridHiddenColumns();

  // Kick off the export-first submission flow:
  // 1. Build the export workbook (ExportRunner)
  // 2. Upload via submission-export
  // 3. Then call the actual rfps/:id/submit endpoint
  const handleSubmitClick = useCallback((): void => {
    const locsWithHidden = selectedCoverageTypes
      .filter((type) =>
        // Coverages with plan variants (Dental PPO/HMO) persist their grid state
        // per variant, so their grid id carries the benefit plan type.
        coveragesWithCompletion.some(
          (coverage) =>
            coverage.type === type &&
            stepState.selectedCoverages.includes(coverage.id) &&
            hiddenColumnsCount(
              coverage.benefitPlanType
                ? `plan-editor-${type}-${coverage.benefitPlanType}`
                : `plan-editor-${type}`,
            ) > 0,
        ),
      )
      .map(
        (type) =>
          coveragesWithCompletion.find((c) => c.type === type)?.label ?? type,
      );
    if (locsWithHidden.length > 0) {
      setHiddenColumnCoverageLabels(locsWithHidden);
      setShowHiddenColumnsDialog(true);
      return;
    }
    beginSubmission();
    setExportStatus("running");
  }, [
    beginSubmission,
    coveragesWithCompletion,
    hiddenColumnsCount,
    selectedCoverageTypes,
    stepState.selectedCoverages,
  ]);

  // Derive display status from submission + export states
  const displayStatus: SubmissionStatus =
    exportStatus === "running" ? "submitting" : submissionStatus;

  const handleNext = (): void => {
    const nextStep = getNextStep(stepState.currentStep);
    if (nextStep) {
      setStepState((prev) => ({ ...prev, currentStep: nextStep }));
    }
  };

  const handleBack = (): void => {
    const previousStep = getPreviousStep(stepState.currentStep);
    if (previousStep) {
      setStepState((prev) => ({ ...prev, currentStep: previousStep }));
    }
  };

  const handleCarriersChange = (carriers: string[]): void => {
    setStepState((prev) => ({ ...prev, selectedCarriers: carriers }));
  };

  const handleCoveragesChange = (coverages: string[]): void => {
    setStepState((prev) => ({ ...prev, selectedCoverages: coverages }));
  };

  const handleOfflineSubmissionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setStepState({
      ...initialStepState,
      isOfflineSubmission: event.target.checked,
    });
    resetOfflineForm({
      offlineReason: undefined,
      offlineDescription: "",
      dateSentToCarriers: startOfDay(new Date()),
      offlinePlansQuoted: Object.fromEntries(
        offlinePlansQuotedCoverages.map((coverage) => [coverage.type, ""]),
      ),
    });
  };

  /**
   * Persists only changed coverage notes to BTX backend.
   * Compares against a baseline snapshot taken when the drawer opened.
   * Also deletes notes for baseline coverages no longer present in the
   * updated set (e.g. a coverage was deselected between saves).
   *
   * Notes are keyed by noteKey (`${coverageId}:${benefitPlanType}`) so plans
   * sharing a coverage id (Dental PPO/HMO) persist independently.
   */
  const saveCoverageNotes = useCallback(
    (
      updatedNotes: Record<string, string>,
      baseline: Record<string, string>,
    ) => {
      for (const [noteKey, content] of Object.entries(updatedNotes)) {
        const previousContent = baseline[noteKey] ?? "";
        const { coverageId, benefitPlanType } = parseCoverageNoteKey(noteKey);

        if (content && content !== previousContent) {
          upsertCoverageNoteMutation.mutate({
            coverageId,
            content,
            benefitPlanType,
          });
        } else if (!content && previousContent) {
          deleteCoverageNoteMutation.mutate({ coverageId, benefitPlanType });
        }
      }

      // Clean up notes for plans that were in the baseline but are no longer
      // in the updated set (e.g. coverage was deselected).
      for (const [noteKey, previousContent] of Object.entries(baseline)) {
        if (previousContent && !(noteKey in updatedNotes)) {
          const { coverageId, benefitPlanType } = parseCoverageNoteKey(noteKey);
          deleteCoverageNoteMutation.mutate({ coverageId, benefitPlanType });
        }
      }
    },
    [upsertCoverageNoteMutation, deleteCoverageNoteMutation],
  );

  // Ref to hold the baseline snapshot of coverage notes when the drawer opens
  const coverageNotesBaselineRef = useRef<Record<string, string>>({});

  const handleOpenNotesDrawer = (tab: NotesTab): void => {
    // Snapshot server-truth as the baseline for dirty comparison
    coverageNotesBaselineRef.current = { ...(baselineCoverageNotes ?? {}) };

    openDialog({
      type: NOTES_DIALOG_TYPE,
      data: {
        ecoRfpId,
        selectedTab: tab,
        rfpNotes,
        initialCoverageNotes: coverageNotes,
        coverages: noteSections,
      },
      width: NOTES_DRAWER_WIDTH,
      widthUnit: NOTES_DRAWER_WIDTH_UNIT,
      onResult: (result) => {
        const {
          rfpNotes: updatedRfpNotes,
          coverageNotes: updatedCoverageNotes,
        } = result as {
          rfpNotes: string;
          coverageNotes: Record<string, string>;
        };
        const allowedNoteKeys = new Set(
          noteSections.map((section) => section.noteKey),
        );
        const filteredCoverageNotes = Object.fromEntries(
          Object.entries(updatedCoverageNotes).filter(([noteKey]) =>
            allowedNoteKeys.has(noteKey),
          ),
        );
        setRfpNotes(updatedRfpNotes);
        setCoverageNotes(filteredCoverageNotes);

        // Persist RFP note if it changed from the server-known value
        const previousRfpNote = savedRfpNote?.content ?? "";
        if (updatedRfpNotes !== previousRfpNote) {
          upsertRfpNoteMutation.mutate({ content: updatedRfpNotes });
        }

        // Only persist notes that actually changed from the baseline
        saveCoverageNotes(
          filteredCoverageNotes,
          coverageNotesBaselineRef.current,
        );
      },
    });
  };

  const isStepCompleted = (step: string): boolean => {
    const currentIndex = STEP_ORDER.indexOf(stepState.currentStep);
    const stepIndex = STEP_ORDER.indexOf(step as typeof stepState.currentStep);
    return stepIndex < currentIndex;
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box>
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            mb: 3,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body-large" component="h2" color="textSecondary">
            <strong>{DRAWER_TITLE}</strong>
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => onClose()}
            size="small"
            sx={{ color: "text.secondary" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ px: 3 }}>
          <SubmissionAlert
            status={displayStatus}
            error={submissionError}
            exportStatus={exportStatus}
            analystDocsFolderUrl={analystDocsFolderUrl}
          />
          {displayStatus === SUBMISSION_IDLE && (
            <>
              <OfflineSubmissionToggle
                checked={stepState.isOfflineSubmission}
                onChange={handleOfflineSubmissionChange}
              />
              <Box sx={{ mt: 3 }}>
                <StepContent
                  isLoading={isLoading}
                  isError={isError}
                  stepState={stepState}
                  carriers={carriers}
                  coveragesWithCompletion={coveragesWithCompletion}
                  clientDetails={clientDetails}
                  brokerageDetails={brokerageDetails}
                  submissionData={submissionData}
                  eligibleEmployees={clientEligibleEmployees}
                  brokerPortalRfpId={brokerPortalRfpId}
                  rfpNotes={rfpNotes}
                  coverageNotes={baselineCoverageNotes ?? {}}
                  coverageNoteSections={noteSections}
                  analystDocsFolderUrl={analystDocsFolderUrl}
                  offlineFormControl={offlineFormControl}
                  offlinePlansQuotedCoverages={offlinePlansQuotedCoverages}
                  onCarriersChange={handleCarriersChange}
                  onCoveragesChange={handleCoveragesChange}
                  onNext={handleNext}
                  onBack={handleBack}
                  onSubmit={handleSubmitClick}
                  onOpenNotesDrawer={handleOpenNotesDrawer}
                  onFieldsChange={setEditedFields}
                  onEditingChange={setIsReviewEditing}
                  isStepCompleted={isStepCompleted}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>
      <Box sx={{ px: 3 }}>
        <SubmitBlockingAlerts
          visible={displayStatus === SUBMISSION_IDLE}
          showCarrierWarning={showCarrierWarning}
          selectedCarriersOverLimit={selectedCarriersOverLimit}
          onEditCarriers={() =>
            setStepState((prev) => ({
              ...prev,
              currentStep: STEP_CARRIERS,
            }))
          }
        />
      </Box>
      <FooterButtons
        status={displayStatus}
        canSubmit={canSubmit}
        isLastStep={isLastStep(stepState.currentStep)}
        missingFields={offlineMissingFields}
        isOfflineMode={stepState.isOfflineSubmission}
        isEditing={isReviewEditing}
        onClose={() => onClose()}
        onSubmit={handleSubmitClick}
      />
      {exportStatus === "running" && ecoRfpId && brokerPortalRfpId && (
        <ExportRunner
          ecoRfpId={ecoRfpId}
          brokerPortalRfpId={brokerPortalRfpId}
          rfpName={submissionData?.clientName}
          coverageTypes={selectedCoverageTypes}
          coveragePlanTypes={selectedCoveragePlanTypes}
          onComplete={async (workbook: ExcelJS.Workbook) => {
            try {
              await uploadSubmissionExport(
                workbook,
                parseInt(brokerPortalRfpId, 10),
              );
              setExportStatus("idle");
              // Export uploaded — now perform the actual submission
              handleSubmit();
            } catch {
              await downloadExport(workbook);
              setExportStatus("upload-failed");
              failSubmission(
                "Export upload failed. The file was downloaded instead.",
              );
            }
          }}
          onError={() => {
            setExportStatus("failed");
            failSubmission("Failed to build the export workbook.");
          }}
        />
      )}
      <ConfirmationHiddenColumnsDialog
        open={showHiddenColumnsDialog}
        coverageLabels={hiddenColumnCoverageLabels}
        onCancel={() => setShowHiddenColumnsDialog(false)}
        onConfirm={() => {
          setShowHiddenColumnsDialog(false);
          beginSubmission();
          setExportStatus("running");
        }}
      />
    </Box>
  );
};

export default SendRfpDrawer;
